export const ageBands = ["unknown", "under75", "over75"] as const;
export type AgeBand = (typeof ageBands)[number];

export const egfrBands = ["unknown", "gt40", "30to40", "20to30", "lt20"] as const;
export type EgfrBand = (typeof egfrBands)[number];

export const sedationStates = ["unknown", "sedated", "extubated"] as const;
export type SedationState = (typeof sedationStates)[number];

export const allergyStatuses = ["unknown", "yes", "no"] as const;
export type AllergyStatus = (typeof allergyStatuses)[number];

export const opioidChoices = ["unknown", "morphine", "oxycodone", "fentanyl", "alfentanil", "remifentanil"] as const;
export type OpioidChoice = (typeof opioidChoices)[number];

export const stageValues = [
  "unknown",
  "before-surgery",
  "during-surgery",
  "handover",
  "itu-sedated",
  "after-extubation",
  "oral-analgesia",
  "breakthrough-pain",
] as const;
export type StageValue = (typeof stageValues)[number];

export const supportedFlags = [
  "chronicPain",
  "chronicGabapentinoid",
  "polyneuropathy",
  "opioidUse",
  "persistentPostOpPainRisk",
  "neuropathicPain",
  "methadone",
  "ivdu",
  "mentalHealth",
  "oralNotTolerated",
  "functionalLimitation",
  "bmiAbove30",
  "weightAbove50",
  "ischaemicHeartDisease",
  "congestiveHeartFailure",
  "cerebrovascularDisease",
  "peripheralVascularDisease",
  "renalImpairment",
  "giBleedingHistory",
  "coagulationDefect",
  "uncontrolledHypertension",
  "highDegreeHeartBlock",
  "severeBradycardia",
  "hypotensionNoradrenaline",
  "paracetamolAllergy",
  "pregabalinAllergy",
  "gabapentinAllergy",
  "morphineAllergy",
  "oxycodoneAllergy",
  "fentanylAllergy",
  "alfentanilAllergy",
  "remifentanilAllergy",
  "ketamineAllergy",
  "dexmedetomidineAllergy",
  "magnesiumAllergy",
  "nsaidAllergy",
  "buprenorphineAllergy",
  "dihydrocodeineAllergy",
] as const;
export type WorkflowFlag = (typeof supportedFlags)[number];

export interface WorkflowState {
  age: AgeBand;
  egfr: EgfrBand;
  stage: StageValue;
  sedation: SedationState;
  pain: number | null;
  cpot: number | null;
  opioid: OpioidChoice;
  route: string | null;
  flags: WorkflowFlag[];
  noFlags: WorkflowFlag[];
  allergies: AllergyStatus;
  choices: string[];
}

export const defaultWorkflowState: WorkflowState = {
  age: "unknown",
  egfr: "unknown",
  stage: "unknown",
  sedation: "unknown",
  pain: null,
  cpot: null,
  opioid: "unknown",
  route: null,
  flags: [],
  noFlags: [],
  allergies: "unknown",
  choices: [],
};

export const urlStateKeys = ["s"] as const;
export const forbiddenUrlStateKeys = ["name", "nhs", "mrn", "dob", "dateOfBirth", "notes", "note", "comment", "comments"];

const compactVersion = "1";
const ageCode = { unknown: "", under75: "u", over75: "o" } satisfies Record<AgeBand, string>;
const egfrCode = { unknown: "", gt40: "4", "30to40": "3", "20to30": "2", lt20: "1" } satisfies Record<EgfrBand, string>;
const stageCode = {
  unknown: "",
  "before-surgery": "b",
  "during-surgery": "d",
  handover: "h",
  "itu-sedated": "i",
  "after-extubation": "e",
  "oral-analgesia": "r",
  "breakthrough-pain": "k",
} satisfies Record<StageValue, string>;
const sedationCode = { unknown: "", sedated: "s", extubated: "e" } satisfies Record<SedationState, string>;
const opioidCode = { unknown: "", morphine: "m", oxycodone: "o", fentanyl: "f", alfentanil: "a", remifentanil: "r" } satisfies Record<OpioidChoice, string>;
const allergyCode = { unknown: "", yes: "y", no: "n" } satisfies Record<AllergyStatus, string>;
const routeCode: Record<string, string> = {
  "/start": "s",
  "/setup": "t",
  "/before-surgery": "b",
  "/during-surgery": "d",
  "/handover": "h",
  "/itu-sedated": "i",
  "/after-extubation": "e",
  "/oral-analgesia": "r",
  "/breakthrough-pain": "k",
  "/tools": "x",
  "/tools/pain-score": "p",
  "/tools/function": "f",
  "/tools/cpot": "c",
};
const choiceCode: Record<string, string> = {
  "pre-gabapentinoid:pregabalin": "p",
  "pre-gabapentinoid:gabapentin": "g",
};

function reverseMap<T extends string>(map: Record<T, string>) {
  return Object.fromEntries(Object.entries(map).map(([key, value]) => [value, key])) as Record<string, T>;
}

const ageFromCode = reverseMap(ageCode);
const egfrFromCode = reverseMap(egfrCode);
const stageFromCode = reverseMap(stageCode);
const sedationFromCode = reverseMap(sedationCode);
const opioidFromCode = reverseMap(opioidCode);
const allergyFromCode = reverseMap(allergyCode);
const routeFromCode = Object.fromEntries(Object.entries(routeCode).map(([path, code]) => [code, path]));
const choiceFromCode = Object.fromEntries(Object.entries(choiceCode).map(([choice, code]) => [code, choice]));

function isOneOf<T extends readonly string[]>(value: string | null, allowed: T): value is T[number] {
  return value !== null && allowed.includes(value);
}

function parseScore(value: string | null, max: number) {
  if (value === null || value.trim() === "") {
    return null;
  }

  const score = Number(value);
  if (!Number.isInteger(score) || score < 0 || score > max) {
    return null;
  }

  return score;
}

function parseFlags(value: string | null): WorkflowFlag[] {
  if (!value) {
    return [];
  }

  const parsed = value
    .split(",")
    .map((flag) => flag.trim())
    .filter((flag): flag is WorkflowFlag => isOneOf(flag, supportedFlags));

  return Array.from(new Set(parsed)).sort();
}

function parseChoices(value: string | null): string[] {
  if (!value) {
    return [];
  }

  return Array.from(
    new Set(
      value
        .split(",")
        .map((choice) => choice.trim())
        .filter((choice) => /^[a-z0-9-]+:[a-z0-9-]+$/i.test(choice)),
    ),
  ).sort();
}

function bitsetForFlags(flags: WorkflowFlag[]) {
  return supportedFlags.reduce((bits, flag, index) => (flags.includes(flag) ? bits | (1n << BigInt(index)) : bits), 0n).toString(36);
}

function bigIntFromBase36(value: string) {
  let result = 0n;
  for (const character of value.toLowerCase()) {
    const parsed = parseInt(character, 36);
    if (Number.isNaN(parsed)) return 0n;
    const digit = BigInt(parsed);
    if (digit < 0n || digit >= 36n) return 0n;
    result = result * 36n + digit;
  }
  return result;
}

function flagsFromBitset(value: string) {
  if (!value) return [];
  const bits = bigIntFromBase36(value);
  return supportedFlags.filter((_, index) => (bits & (1n << BigInt(index))) !== 0n);
}

function parseCompactState(value: string | null): WorkflowState | null {
  if (!value) return null;
  const [version, age, egfr, stage, sedation, pain, cpot, opioid, allergies, flags, noFlags, choices, route] = value.split(".");
  if (version !== compactVersion) return null;

  return {
    ...defaultWorkflowState,
    age: ageFromCode[age] ?? defaultWorkflowState.age,
    egfr: egfrFromCode[egfr] ?? defaultWorkflowState.egfr,
    stage: stageFromCode[stage] ?? defaultWorkflowState.stage,
    sedation: sedationFromCode[sedation] ?? defaultWorkflowState.sedation,
    pain: parseScore(pain ?? null, 10),
    cpot: parseScore(cpot ?? null, 8),
    opioid: opioidFromCode[opioid] ?? defaultWorkflowState.opioid,
    route: routeFromCode[route] ?? defaultWorkflowState.route,
    flags: flagsFromBitset(flags ?? ""),
    noFlags: flagsFromBitset(noFlags ?? ""),
    allergies: allergyFromCode[allergies] ?? defaultWorkflowState.allergies,
    choices: (choices ?? "")
      .split("")
      .map((choice) => choiceFromCode[choice])
      .filter((choice): choice is string => choice !== undefined)
      .sort(),
  };
}

function compactChoices(choices: string[]) {
  return choices
    .map((choice) => choiceCode[choice])
    .filter((choice): choice is string => choice !== undefined)
    .sort()
    .join("");
}

export function parseWorkflowState(search: string | URLSearchParams): WorkflowState {
  const params = typeof search === "string" ? new URLSearchParams(search) : search;
  const compact = parseCompactState(params.get("s"));
  if (compact) return compact;

  const age = params.get("age");
  const egfr = params.get("egfr");
  const stage = params.get("stage");
  const sedation = params.get("sedation");
  const opioid = params.get("opioid");
  const route = params.get("route");
  const allergies = params.get("allergies");

  return {
    age: isOneOf(age, ageBands) ? age : defaultWorkflowState.age,
    egfr: isOneOf(egfr, egfrBands) ? egfr : defaultWorkflowState.egfr,
    stage: isOneOf(stage, stageValues) ? stage : defaultWorkflowState.stage,
    sedation: isOneOf(sedation, sedationStates) ? sedation : defaultWorkflowState.sedation,
    pain: parseScore(params.get("pain"), 10),
    cpot: parseScore(params.get("cpot"), 8),
    opioid: isOneOf(opioid, opioidChoices) ? opioid : defaultWorkflowState.opioid,
    route: route && /^[a-z0-9-/]+$/i.test(route) ? route : defaultWorkflowState.route,
    flags: parseFlags(params.get("flags")),
    noFlags: parseFlags(params.get("noFlags")),
    allergies: isOneOf(allergies, allergyStatuses) ? allergies : defaultWorkflowState.allergies,
    choices: parseChoices(params.get("choices")),
  };
}

export function encodeWorkflowState(state: WorkflowState): string {
  const params = new URLSearchParams();

  const fields = [
    compactVersion,
    ageCode[state.age],
    egfrCode[state.egfr],
    stageCode[state.stage],
    sedationCode[state.sedation],
    state.pain === null ? "" : String(state.pain),
    state.cpot === null ? "" : String(state.cpot),
    opioidCode[state.opioid],
    allergyCode[state.allergies],
    state.flags.length > 0 ? bitsetForFlags(state.flags) : "",
    state.noFlags.length > 0 ? bitsetForFlags(state.noFlags) : "",
    compactChoices(state.choices),
    state.route ? routeCode[state.route] ?? "" : "",
  ];
  while (fields.length > 1 && fields[fields.length - 1] === "") {
    fields.pop();
  }

  if (fields.length > 1) params.set("s", fields.join("."));

  return params.toString();
}

export function mergeWorkflowState(state: WorkflowState, patch: Partial<WorkflowState>): WorkflowState {
  return {
    ...state,
    ...patch,
    flags: patch.flags ? Array.from(new Set(patch.flags)).sort() : state.flags,
    noFlags: patch.noFlags ? Array.from(new Set(patch.noFlags)).sort() : state.noFlags,
    choices: patch.choices ? Array.from(new Set(patch.choices)).sort() : state.choices,
  };
}

export function buildUrl(path: string, state: WorkflowState) {
  const query = encodeWorkflowState(state);
  return query ? `${path}?${query}` : path;
}

export function isEgfrAbove30(egfr: EgfrBand) {
  return egfr === "gt40" || egfr === "30to40";
}

export function isEgfrAbove40(egfr: EgfrBand) {
  return egfr === "gt40";
}

export function isEgfrBelow30(egfr: EgfrBand) {
  return egfr === "20to30" || egfr === "lt20";
}

export function painBand(score: number | null) {
  if (score === null) return "not set";
  if (score === 0) return "no pain";
  if (score <= 3) return "mild pain";
  if (score <= 6) return "moderate pain";
  return "severe pain";
}
